import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { TabsContent } from '../ui/tabs';
import { Combobox } from '../ui/combobox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Package, Pill, Pencil, Play, Pause, StopCircle, Plus, FileText, Upload, Loader2, Check, AlertCircle } from 'lucide-react';
import { formatDate } from '../../lib/utils';

export const TabConsumo = ({
  consumoRegras,
  estoqueItens,
  showConsumoModal,
  setShowConsumoModal,
  editingConsumo,
  consumoForm,
  setConsumoForm,
  handleAddConsumo,
  handleEditConsumo,
  handlePauseConsumo,
  handleEndConsumo,
  resetConsumoForm,
  showPrescricaoModal,
  setShowPrescricaoModal,
  prescricaoFile,
  setPrescricaoFile,
  prescricaoResult,
  setPrescricaoResult,
  prescricaoLoading,
  selectedPrescricaoItens,
  setSelectedPrescricaoItens,
  handleImportPrescricao,
  handleCreateRegrasPrescricao,
  togglePrescricaoItem
}) => (
  <>
    <TabsContent value="consumo">
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Package className="h-5 w-5" />
                Consumo (medicamentos/materiais)
              </CardTitle>
              <CardDescription>
                Regras de consumo diário automático de estoque
              </CardDescription>
            </div>
            <Button onClick={() => { resetConsumoForm(); setShowConsumoModal(true); }} data-testid="btn-adicionar-consumo">
              <Plus className="h-4 w-4 mr-2" />
              Adicionar consumo
            </Button>
            <Button 
              variant="outline" 
              onClick={() => setShowPrescricaoModal(true)}
              className="gap-2 border-purple-300 text-purple-700 hover:bg-purple-50"
              data-testid="btn-importar-prescricao"
            >
              <FileText className="h-4 w-4" />
              Importar Prescrição (IA)
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {consumoRegras.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Package className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>Nenhuma regra de consumo cadastrada</p>
              <p className="text-sm">Adicione itens de estoque para dedução automática diária</p>
            </div>
          ) : (
            <div className="border rounded-md overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left p-3 font-medium">Item</th>
                    <th className="text-right p-3 font-medium">Qtd/Dia</th>
                    <th className="text-center p-3 font-medium">Data Início</th>
                    <th className="text-center p-3 font-medium">Data Fim</th>
                    <th className="text-center p-3 font-medium">Status</th>
                    <th className="text-center p-3 font-medium">Tipo</th>
                    <th className="text-center p-3 font-medium">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {consumoRegras.map(regra => (
                    <tr key={regra.id} className="border-t">
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          {estoqueItens.find(i => i.id === regra.item_id)?.tipo === 'medicamento' ? (
                            <Pill className="h-4 w-4 text-purple-500" />
                          ) : (
                            <Package className="h-4 w-4 text-blue-500" />
                          )}
                          <span className="font-medium">{regra.item_nome}</span>
                        </div>
                        {regra.observacoes && (
                          <p className="text-xs text-muted-foreground mt-1">{regra.observacoes}</p>
                        )}
                      </td>
                      <td className="p-3 text-right">
                        {regra.quantidade_por_dia} {regra.item_unidade}
                      </td>
                      <td className="p-3 text-center">
                        {formatDate(regra.data_inicio)}
                      </td>
                      <td className="p-3 text-center">
                        {regra.data_fim ? formatDate(regra.data_fim) : '-'}
                      </td>
                      <td className="p-3 text-center">
                        <Badge variant={
                          regra.status === 'ativo' ? 'default' :
                          regra.status === 'pausado' ? 'secondary' : 'outline'
                        } className={
                          regra.status === 'ativo' ? 'bg-green-100 text-green-800' :
                          regra.status === 'pausado' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-gray-100 text-gray-800'
                        }>
                          {regra.status === 'ativo' ? 'Ativo' :
                           regra.status === 'pausado' ? 'Pausado' : 'Encerrado'}
                        </Badge>
                      </td>
                      <td className="p-3 text-center">
                        <Badge variant="outline" className={
                          regra.tipo_uso === 'se_necessario'
                            ? 'bg-amber-50 text-amber-700 border-amber-200'
                            : 'bg-blue-50 text-blue-700 border-blue-200'
                        }>
                          {regra.tipo_uso === 'se_necessario' ? 'SN' : 'Prog.'}
                        </Badge>
                      </td>
                      <td className="p-3">
                        <div className="flex justify-center gap-1">
                          {regra.status !== 'encerrado' && (
                            <>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => handleEditConsumo(regra)}
                                title="Editar"
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => handlePauseConsumo(regra.id, regra.status)}
                                title={regra.status === 'pausado' ? 'Reativar' : 'Pausar'}
                              >
                                {regra.status === 'pausado' ? (
                                  <Play className="h-4 w-4 text-green-600" />
                                ) : (
                                  <Pause className="h-4 w-4 text-yellow-600" />
                                )}
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => handleEndConsumo(regra.id)}
                                title="Encerrar"
                              >
                                <StopCircle className="h-4 w-4 text-red-500" />
                              </Button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </TabsContent>

    {/* Consumo Modal */}
    <Dialog open={showConsumoModal} onOpenChange={setShowConsumoModal}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {editingConsumo ? 'Editar Regra de Consumo' : 'Adicionar Consumo'}
          </DialogTitle>
          <DialogDescription>
            Configure o consumo diário de materiais/medicamentos para esta internação
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="space-y-2">
            <Label>Item do estoque *</Label>
            {(() => {
              const activeItems = estoqueItens.filter(i => i.ativo !== false);
              const options = activeItems.map(item => ({
                value: item.id,
                label: `${item.nome} (${item.estoque_atual || 0} ${item.unidade || 'un'})`,
                subtitle: item.tipo === 'medicamento' ? 'Medicamento' : 'Material',
                badge: item.categoria || undefined
              }));
              return (
                <>
                  <Combobox
                    options={options}
                    value={consumoForm.item_id}
                    onValueChange={(v) => setConsumoForm(prev => ({ ...prev, item_id: v }))}
                    placeholder="Selecione o item..."
                    searchPlaceholder="Buscar medicamento ou material..."
                    emptyText="Nenhum item encontrado"
                    disabled={!!editingConsumo}
                    data-testid="combobox-item-estoque"
                  />
                  <p className="text-xs text-muted-foreground">
                    Itens disponiveis: {activeItems.length}
                    {activeItems.length === 0 && (
                      <span className="text-amber-600 font-medium ml-1">
                        — Nenhum item ativo encontrado no estoque.
                      </span>
                    )}
                  </p>
                </>
              );
            })()}
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Tipo *</Label>
              <Select
                value={consumoForm.tipo_uso || 'programado'}
                onValueChange={(v) => setConsumoForm(prev => ({ ...prev, tipo_uso: v }))}
              >
                <SelectTrigger data-testid="select-tipo-uso">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="programado">Programado</SelectItem>
                  <SelectItem value="se_necessario">Se necessário (PRN)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Frequência *</Label>
              <Select
                value={consumoForm.frequencia}
                onValueChange={(v) => setConsumoForm(prev => ({ ...prev, frequencia: v }))}
              >
                <SelectTrigger data-testid="select-frequencia">
                  <SelectValue placeholder="Selecione..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="6/6h">6/6h (4x ao dia)</SelectItem>
                  <SelectItem value="8/8h">8/8h (3x ao dia)</SelectItem>
                  <SelectItem value="12/12h">12/12h (2x ao dia)</SelectItem>
                  <SelectItem value="1x ao dia">1x ao dia</SelectItem>
                  <SelectItem value="2x ao dia">2x ao dia</SelectItem>
                  <SelectItem value="3x ao dia">3x ao dia</SelectItem>
                  <SelectItem value="SOS">SOS (se necessário)</SelectItem>
                  <SelectItem value="Contínuo">Contínuo (24h)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Qtd por aplicação</Label>
              <Input
                type="number"
                step="0.5"
                min="0.5"
                value={consumoForm.quantidade_por_aplicacao}
                onChange={(e) => setConsumoForm(prev => ({ ...prev, quantidade_por_aplicacao: e.target.value }))}
                placeholder="1"
                data-testid="input-qtd-aplicacao"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Data de início *</Label>
              <Input
                type="date"
                value={consumoForm.data_inicio}
                onChange={(e) => setConsumoForm(prev => ({ ...prev, data_inicio: e.target.value }))}
                disabled={!!editingConsumo}
              />
            </div>
            <div className="space-y-2">
              <Label>Data de fim (opcional)</Label>
              <Input
                type="date"
                value={consumoForm.data_fim}
                onChange={(e) => setConsumoForm(prev => ({ ...prev, data_fim: e.target.value }))}
              />
            </div>
          </div>
          <p className="text-xs text-muted-foreground -mt-2">
            Deixe "Data de fim" em branco para consumo contínuo
          </p>
          <div className="space-y-2">
            <Label>Observações</Label>
            <Textarea
              value={consumoForm.observacoes}
              onChange={(e) => setConsumoForm(prev => ({ ...prev, observacoes: e.target.value }))}
              placeholder="Ex: Uso conforme prescrição médica"
              rows={2}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setShowConsumoModal(false)}>Cancelar</Button>
          <Button 
            onClick={handleAddConsumo}
            disabled={!consumoForm.item_id || !consumoForm.frequencia}
            data-testid="btn-confirmar-consumo"
          >
            {editingConsumo ? 'Salvar' : 'Adicionar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {/* Prescrição IA Modal */}
    <Dialog open={showPrescricaoModal} onOpenChange={(open) => {
      if (!open) {
        setPrescricaoFile(null);
        setPrescricaoResult(null);
        setSelectedPrescricaoItens([]);
      }
      setShowPrescricaoModal(open);
    }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-purple-600" />
            Importar Prescrição com IA
          </DialogTitle>
          <DialogDescription>
            Faça upload de uma prescrição médica (PDF ou imagem) para extrair automaticamente os medicamentos e criar regras de consumo.
          </DialogDescription>
        </DialogHeader>

        {!prescricaoResult ? (
          <div className="space-y-4 py-4">
            <div className="border-2 border-dashed rounded-lg p-6 text-center">
              <input
                type="file"
                id="prescricao-file"
                className="hidden"
                accept=".pdf,.jpg,.jpeg,.png,.webp"
                onChange={(e) => setPrescricaoFile(e.target.files[0])}
              />
              <label htmlFor="prescricao-file" className="cursor-pointer">
                {prescricaoFile ? (
                  <div className="flex items-center justify-center gap-2">
                    <FileText className="h-8 w-8 text-purple-600" />
                    <div className="text-left">
                      <p className="font-medium">{prescricaoFile.name}</p>
                      <p className="text-sm text-muted-foreground">
                        {(prescricaoFile.size / 1024).toFixed(1)} KB
                      </p>
                    </div>
                  </div>
                ) : (
                  <>
                    <Upload className="h-10 w-10 mx-auto text-muted-foreground mb-2" />
                    <p className="font-medium">Clique para selecionar arquivo</p>
                    <p className="text-sm text-muted-foreground">PDF, JPG, PNG ou WEBP</p>
                  </>
                )}
              </label>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowPrescricaoModal(false)}>
                Cancelar
              </Button>
              <Button 
                onClick={handleImportPrescricao} 
                disabled={!prescricaoFile || prescricaoLoading}
                className="gap-2"
              >
                {prescricaoLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Processando...
                  </>
                ) : (
                  <>
                    <FileText className="h-4 w-4" />
                    Analisar com IA
                  </>
                )}
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-3 gap-3">
              <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg text-center">
                <p className="text-2xl font-bold text-blue-600">{prescricaoResult.resumo.total_itens}</p>
                <p className="text-xs text-muted-foreground">Total de itens</p>
              </div>
              <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg text-center">
                <p className="text-2xl font-bold text-green-600">{prescricaoResult.resumo.encontrados}</p>
                <p className="text-xs text-muted-foreground">No estoque</p>
              </div>
              <div className="p-3 bg-orange-50 dark:bg-orange-900/20 rounded-lg text-center">
                <p className="text-2xl font-bold text-orange-600">{prescricaoResult.resumo.nao_encontrados}</p>
                <p className="text-xs text-muted-foreground">Não encontrados</p>
              </div>
            </div>

            {prescricaoResult.resumo.medico_nome && (
              <div className="p-3 bg-muted/50 rounded-lg text-sm">
                <span className="text-muted-foreground">Médico: </span>
                <span className="font-medium">{prescricaoResult.resumo.medico_nome}</span>
                {prescricaoResult.resumo.crm && ` (CRM: ${prescricaoResult.resumo.crm})`}
              </div>
            )}

            {prescricaoResult.itens_encontrados.length > 0 && (
              <div className="space-y-2">
                <h4 className="font-medium text-green-700 flex items-center gap-2">
                  <Check className="h-4 w-4" />
                  Itens encontrados no estoque ({prescricaoResult.itens_encontrados.length})
                </h4>
                <div className="border rounded-lg divide-y max-h-48 overflow-y-auto">
                  {prescricaoResult.itens_encontrados.map((item, idx) => (
                    <div 
                      key={idx} 
                      className={`p-3 flex items-center gap-3 cursor-pointer hover:bg-muted/50 ${
                        selectedPrescricaoItens.includes(item.item_id_match) ? 'bg-green-50 dark:bg-green-900/20' : ''
                      }`}
                      onClick={() => togglePrescricaoItem(item.item_id_match)}
                    >
                      <input
                        type="checkbox"
                        checked={selectedPrescricaoItens.includes(item.item_id_match)}
                        onChange={() => {}}
                        className="h-4 w-4 rounded border-gray-300"
                      />
                      <div className="flex-1">
                        <p className="font-medium text-sm">{item.nome}</p>
                        <p className="text-xs text-muted-foreground">
                          Estoque: {item.item_nome_match}
                          {item.quantidade_diaria && ` • ${item.quantidade_diaria}/dia`}
                          {item.frequencia && ` • ${item.frequencia}`}
                          {item.via && ` • Via: ${item.via}`}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {prescricaoResult.itens_nao_encontrados.length > 0 && (
              <div className="space-y-2">
                <h4 className="font-medium text-orange-700 flex items-center gap-2">
                  <AlertCircle className="h-4 w-4" />
                  Itens NÃO encontrados no estoque ({prescricaoResult.itens_nao_encontrados.length})
                </h4>
                <div className="border border-orange-200 rounded-lg divide-y max-h-36 overflow-y-auto">
                  {prescricaoResult.itens_nao_encontrados.map((item, idx) => (
                    <div key={idx} className="p-3 bg-orange-50/50 dark:bg-orange-900/10">
                      <p className="font-medium text-sm">{item.nome}</p>
                      <p className="text-xs text-orange-600">
                        {item.quantidade_diaria && `${item.quantidade_diaria}/dia`}
                        {item.frequencia && ` • ${item.frequencia}`}
                        {item.via && ` • Via: ${item.via}`}
                      </p>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-orange-600">
                  Cadastre estes itens no estoque para incluí-los no consumo
                </p>
              </div>
            )}

            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => {
                setPrescricaoResult(null);
                setPrescricaoFile(null);
              }}>
                Nova importação
              </Button>
              <Button 
                onClick={handleCreateRegrasPrescricao}
                disabled={selectedPrescricaoItens.length === 0 || prescricaoLoading}
                className="gap-2"
              >
                {prescricaoLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Plus className="h-4 w-4" />
                )}
                Criar {selectedPrescricaoItens.length} regras de consumo
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  </>
);
